import type { SessionAbortReason } from '@atlassian/editor-ai-common/analytics/normalized-telemetry-event';

/**
 * Why the bridge is aborting a streaming command.
 *
 * This is the bridge's own vocabulary, deliberately kept separate from the
 * telemetry-facing {@link SessionAbortReason}:
 *
 * - `user_abort`: the user explicitly cancelled.
 * - `timeout`: the inactivity timeout elapsed with no stream activity.
 * - `error`: an error was thrown while processing the stream.
 * - `aborted`: abort with no known cause.
 */
export type AbortReason = 'user_abort' | 'aborted' | 'timeout' | 'error';

/**
 * Subset of abort reasons understood by the remix experience-tracking
 * contract (`streamAbortReason`).
 */
export type StreamAbortReason = 'user_abort' | 'aborted';

/**
 * Extra context that travels with an abort. Kept as an options bag so further
 * context can be added without reshaping every signature in the abort chain.
 */
export type AbortOptions = {
	/** Redacted error message, only meaningful when the reason is `error`. */
	errorMessage?: string;
	isExplicitUserCancellation?: boolean;
};

/**
 * Narrow a bridge {@link AbortReason} down to the {@link StreamAbortReason}
 * accepted by the remix experience-tracking contract. Keeps the two
 * vocabularies decoupled so the bridge can add reasons without reshaping the
 * experience-tracking enum.
 */
export function toStreamAbortReason(reason?: AbortReason): StreamAbortReason | undefined {
	switch (reason) {
		case 'user_abort':
			return 'user_abort';
		case 'aborted':
		case 'timeout':
		case 'error':
			// The remix contract has no dedicated value for these, so they
			// collapse into the generic `aborted` reason.
			return 'aborted';
		case undefined:
		default:
			return undefined;
	}
}

/**
 * Translate a bridge {@link AbortReason} into the telemetry-contract
 * {@link SessionAbortReason}. Keeps the two vocabularies decoupled so the
 * bridge can add reasons without reshaping the telemetry enum, and so
 * `undefined` has one well-defined meaning (`unknown`) instead of being
 * handled ad hoc at each call site.
 */
export function toSessionAbortReason(reason?: AbortReason): SessionAbortReason {
	switch (reason) {
		case 'user_abort':
			return 'user_abort';
		case 'timeout':
			return 'timeout';
		case 'error':
			return 'aborted_due_to_error';
		case 'aborted':
		case undefined:
		default:
			return 'unknown';
	}
}
